import React, { useState } from "react";
import ComplaintService, {
  PatientComplaint,
} from "../../../../services/Complaintservice";

interface ComplaintsHistoryProps {
  complaints: PatientComplaint[];
  onEdit: (complaint: PatientComplaint) => void;
  onDelete: (complaintId: number) => void;
  loading?: boolean;
}

const ComplaintsHistory: React.FC<ComplaintsHistoryProps> = ({
  complaints,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [viewingPdfId, setViewingPdfId] = useState<number | null>(null);

  const handleDelete = async (complaintId: number) => {
    if (!window.confirm("Are you sure you want to delete this complaint?")) {
      return;
    }

    setDeletingId(complaintId);
    try {
      await ComplaintService.deleteComplaint(complaintId);
      onDelete(complaintId);
    } catch (error) {
      console.error("Error deleting complaint:", error);
      alert("Failed to delete complaint");
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewPDF = (complaintId: number) => {
    setViewingPdfId(complaintId);
    try {
      ComplaintService.generatePDF(complaintId);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF");
    } finally {
      // Reset after a short delay
      setTimeout(() => setViewingPdfId(null), 1000);
    }
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" | "success" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#3b82f6", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      danger: { background: "#ef4444", color: "white" },
      success: { background: "#10b981", color: "white" },
    };

    return {
      padding: "6px 12px",
      borderRadius: "6px",
      border: "none",
      fontSize: "12px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      ...variants[variant],
      minWidth: "80px",
      textAlign: "center" as const,
    };
  };

  if (loading) {
    return (
      <div className="complaints-history-container">
        <div className="loading-state">
          <div className="spinner" />
          Loading complaints...
        </div>
      </div>
    );
  }

  if (complaints.length === 0) {
    return (
      <div className="complaints-history-container">
        <div className="empty-state">💬 No complaints recorded yet.</div>
      </div>
    );
  }

  return (
    <>
      <div className="complaints-history-container">
        {/* Header */}
        <div className="complaints-history-header">
          <h2>💬 Complaints History</h2>
          <p>All recorded patient complaints</p>
        </div>

        {/* Content */}
        <div className="complaints-history-content">
          <div className="complaints-list">
            {complaints.map((complaint) => (
              <div key={complaint.id} className="complaint-card">
                {/* Complaint Header */}
                <div className="complaint-header">
                  <div className="complaint-info">
                    <div className="complaint-title">
                      <h3>
                        <span>📝</span>
                        Complaint Record
                      </h3>
                      {complaint.is_recent && (
                        <div className="badges">
                          <span className="recent-badge">Recent</span>
                        </div>
                      )}
                    </div>

                    <div className="complaint-details">
                      <div className="detail-item">
                        <span className="detail-icon">📅</span>
                        <strong>Recorded:</strong>
                        <span>
                          {complaint.recorded_at_formatted ||
                            ComplaintService.formatDateTime(
                              complaint.recorded_at
                            )}
                        </span>
                      </div>
                      {complaint.recorded_by && (
                        <div className="detail-item">
                          <span className="detail-icon">👤</span>
                          <strong>By:</strong>
                          <span>
                            {complaint.recorded_by.name}
                            {complaint.recorded_by.role &&
                              ` (${complaint.recorded_by.role})`}
                          </span>
                        </div>
                      )}
                      {complaint.time_since_recorded && (
                        <div className="detail-item">
                          <span className="detail-icon">🕒</span>
                          <strong>Time ago:</strong>
                          <span>{complaint.time_since_recorded}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="action-buttons">
                    <button
                      style={buttonStyle("success")}
                      onClick={() =>
                        complaint.id && handleViewPDF(complaint.id)
                      }
                      disabled={viewingPdfId === complaint.id}
                    >
                      {viewingPdfId === complaint.id ? (
                        <span className="button-spinner" />
                      ) : (
                        "📄 View PDF"
                      )}
                    </button>
                    <button
                      style={buttonStyle("primary")}
                      onClick={() => onEdit(complaint)}
                      disabled={deletingId === complaint.id}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      style={buttonStyle("danger")}
                      onClick={() => complaint.id && handleDelete(complaint.id)}
                      disabled={deletingId === complaint.id}
                    >
                      {deletingId === complaint.id ? (
                        <span className="button-spinner" />
                      ) : (
                        "🗑️ Delete"
                      )}
                    </button>
                  </div>
                </div>

                {/* Complaint Body */}
                <div className="complaint-body">
                  <div className="complaint-text-section">
                    <h4>Complaint Details</h4>
                    <div className="complaint-text">{complaint.complaint}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>
        {`
        .complaints-history-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          margin-bottom: 24px;
        }

        .complaints-history-header {
          padding: 16px;
          border-bottom: 1px solid #e5e7eb;
        }

        .complaints-history-header h2 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #1f2937;
        }

        .complaints-history-header p {
          font-size: 13px;
          color: #6b7280;
          margin: 0;
        }

        .complaints-history-content {
          padding: 16px;
        }

        .loading-state {
          text-align: center;
          padding: 40px 20px;
          color: #6b7280;
          font-size: 14px;
        }

        .spinner {
          width: 24px;
          height: 24px;
          border: 2px solid #e2e8f0;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 12px;
        }

        .button-spinner {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          display: inline-block;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #6b7280;
          font-size: 14px;
        }

        .error-state {
          text-align: center;
          padding: 40px 20px;
          color: #ef4444;
          font-size: 14px;
        }

        .complaints-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .complaint-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
          transition: box-shadow 0.2s ease;
        }

        .complaint-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .complaint-header {
          padding: 16px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .complaint-info {
          flex: 1;
        }

        .complaint-title {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 12px;
        }

        .complaint-title h3 {
          font-size: 15px;
          font-weight: 600;
          margin: 0;
          color: #1f2937;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .badges {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .recent-badge {
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 500;
          text-transform: uppercase;
          white-space: nowrap;
          background: #dbeafe;
          color: #1e40af;
        }

        .complaint-details {
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
          font-size: 13px;
          color: #6b7280;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .detail-icon {
          font-size: 12px;
        }

        .detail-item strong {
          color: #374151;
          font-weight: 500;
          margin-right: 4px;
        }

        .detail-item span:last-child {
          color: #6b7280;
        }

        .action-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: flex-start;
        }

        .complaint-body {
          padding: 16px;
        }

        .complaint-text-section h4 {
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 12px 0;
          color: #374151;
        }

        .complaint-text {
          background: #f9fafb;
          border: 1px solid #f3f4f6;
          border-radius: 6px;
          padding: 12px;
          font-size: 13px;
          color: #1f2937;
          line-height: 1.6;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Tablet styles */
        @media (min-width: 640px) {
          .complaints-history-container {
            padding: 20px;
          }

          .complaints-history-header {
            padding: 20px;
          }

          .complaints-history-header h2 {
            font-size: 20px;
          }

          .complaints-history-header p {
            font-size: 14px;
          }

          .complaints-history-content {
            padding: 20px;
          }

          .complaint-header {
            flex-direction: row;
            align-items: flex-start;
          }

          .complaint-title {
            flex-direction: row;
            align-items: center;
          }

          .badges {
            margin-left: 12px;
          }

          .complaint-details {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            font-size: 14px;
          }

          .action-buttons {
            flex-direction: column;
            margin-left: 16px;
          }
        }

        /* Desktop styles */
        @media (min-width: 1024px) {
          .complaint-details {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}
      </style>
    </>
  );
};

export default ComplaintsHistory;
